import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Col, Row, Layout, message } from 'antd';
import { get } from 'lodash';
import CategoryApi from 'services/category';
import PdButton from 'components/pd-button';
import { PageSpinner } from 'components/page-spinner';
import { DndContext, closestCenter, useSensors, useSensor, PointerSensor } from '@dnd-kit/core';
import { SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import './style.scss';
import CustomTreeCategory from 'components/TreeCategory';

const { Content } = Layout;

export function CategoryList() {
  const [loadingPage, setLoadingPage] = useState(true);
  const [limit] = useState(10);
  const [category, setCategory] = useState([]);
  const [listOpen, setListOpen] = useState([]);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        delay: 200,
        tolerance: 5,
      },
    })
  );
  async function getCategoryList() {
    try {
      setLoadingPage(true);
      const { data } = await CategoryApi.getCategoryList();
      const cate = data.data;
      setCategory(cate);
    } catch (err) {
      const errMessage = get(
        get(get(err, 'response', {}), 'data', {}),
        'message',
        ''
      );
      if (errMessage) {
        message.error(errMessage);
      } else {
        message.error('Cannot connect to server, please check connection');
      }
    } finally {
      setLoadingPage(false);
    }
  }

  useEffect(() => {
    getCategoryList();
  }, [limit]);

  if (loadingPage) {
    return <PageSpinner />;
  }

  async function updateCategory(val, index1, index2) {
    const newCategory = category.slice();
    if (index2 !== undefined) {
      newCategory[index1]['subcategories'][index2]['subcategories'] = val;
    } else {
      newCategory[index1]['subcategories'] = val;
    }
    try {
      setCategory(newCategory);
      await CategoryApi.updateCategoryOrder(val);
      message.success('Successfully updated category order');
    } catch {
      message.error('Cannot connect to server, please check connection');
    }
  }

  function openCloseCategory(type, id) {
    const newList = listOpen.slice();
    if (type === 'open') {
      newList.push(id);
      setListOpen(newList);
    } else {
      const index = newList.indexOf(id);
      newList.splice(index, 1);
      setListOpen(newList);
    }
  }

  function SortableItem({ value: category, valIndex, index }) {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
      id: category.id || index,
    });

    const style = {
      transform: CSS.Transform.toString(transform),
      transition,
      opacity: isDragging ? 0.5 : 1,
      marginTop: 20,
      userSelect: 'none',
    };

    return (
      <div
        ref={setNodeRef}
        style={style}
        {...attributes}
        className="pd-cms-category--list draggable-item">
        <Row type="flex" align="middle" gutter={30}>
          <Col span={1}>
            <div {...listeners} style={{ cursor: 'grab', padding: '0 10px' }}>
              ⋮⋮
            </div>
          </Col>
          <Col span={23} {...listeners}>
            <CustomTreeCategory
              data={category}
              updateCategory={updateCategory}
              parentIndex={valIndex}
              listOpen={listOpen}
              openClose={openCloseCategory}
              getCategories={getCategoryList}
            />
          </Col>
        </Row>
      </div>
    );
  }

  function SortableList({ items, onSortEnd }) {
    const handleDragEnd = (event) => {
      const { active, over } = event;
      if (active && over && active.id !== over.id) {
        const oldIndex = items.findIndex((item, index) => (item.id || index) === active.id);
        const newIndex = items.findIndex((item, index) => (item.id || index) === over.id);
        onSortEnd({ oldIndex, newIndex });
      }
    };

    return (
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={items.map((item, index) => ({ id: item.id || index }))} strategy={verticalListSortingStrategy}>
          {items.map((item, index) => {
            return (
              <SortableItem
                key={item.id}
                index={index}
                valIndex={index}
                value={item}
              />
            );
          })}
        </SortableContext>
      </DndContext>
    );
  }

  async function onSortEnd({ oldIndex, newIndex }) {
    let list = [...category];
    let temp = list[oldIndex];
    list.splice(oldIndex, 1);
    list.splice(newIndex, 0, temp);
    let payload = [];
    list.forEach((item, index) => {
      let order_attr = {
        id: item.id,
        order_level: index,
      };
      payload.push(order_attr);
    });

    try {
      setCategory(list);
      await CategoryApi.updateCategoryOrder(payload);
      message.success('Successfully updated category order');
    } catch {
      message.error('Cannot connect to server, please check connection');
    }
  }

  return (
    <Layout className="pd-cms-category main-container">
      <div className="container">
        <div className="content">
          <div className="pageTitle">Categories</div>
          <Content className="pageContent">
            <Row gutter={20}>
              <Col span={24}>
                <Row gutter={30} className="no-margin">
                  <Col span={17}></Col>
                  <Col span={7} className=" float-right">
                    <Link to={'/categories/create'}>
                      <PdButton
                        type="primary"
                        outline
                        style={{ height: 32, width: 176 }}>
                        Add New Category
                      </PdButton>
                    </Link>
                  </Col>
                </Row>
              </Col>
              <Col span={24}>
                <div id="drag-content">
                  <SortableList
                    items={category}
                    onSortEnd={onSortEnd}
                  />
                </div>
              </Col>
            </Row>
          </Content>
        </div>
      </div>
    </Layout>
  );
}

export default { CategoryList };
